// ============================================================
// VERTICAL DEMO FLEETS — distinct customers by use case
// ============================================================
// Three believable demo customers, each with its own fleet shape so
// "View as customer" / org-switch shows genuinely different data:
//
//   1. OIL & GAS    (LARGE  · ~64 sites) — wellsites, rigs, compressor
//      stations, offshore platforms. Mission-critical SLAs, heavy data
//      (SCADA + crew connectivity), remote/harsh locations.
//   2. LOGISTICS    (MEDIUM · ~24 units) — vehicle-mounted, in-motion.
//      Mobile Priority plans, cellular-primary + Starlink failover.
//   3. RETAIL       (SMALL  · ~10 stores) — storefront Starlink as the
//      backup link. Low Starlink usage (only when the primary drops),
//      Business-Standard / backup-tier plans.
//
// Each terminal matches the shape produced by enterprise-fleet.js so the
// existing pages render without changes. Resolved per account number by
// starlink-api.js.

// ─── Seeded RNG (deterministic per fleet) ────────────────────
function makeRng(seed) {
  let s = seed % 233280;
  return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
}

// ─── Generalized fleet generator ─────────────────────────────
// profile: { prefix, count, seed, regions[], siteTypes[], slaMix[],
//            onlineBias, usageGB:[lo,hi], allocFromPlan, costGB:[lo,hi] }
function buildFleet(profile) {
  const rand = makeRng(profile.seed);
  const pick = (arr) => arr[Math.floor(rand() * arr.length)];
  const between = (lo, hi) => lo + rand() * (hi - lo);

  return Array.from({ length: profile.count }, (_, i) => {
    const region = pick(profile.regions);
    const [cityName, lat, lon] = pick(region.cities);
    const siteType = pick(profile.siteTypes);
    const sla = pick(profile.slaMix);
    const onlineRoll = rand();
    const onlineBias = profile.onlineBias ?? 0.92;
    // Healthy-but-real demo posture: most sites online, a thin degraded
    // band, and only a small offline tail (top ~3%). No fleet should look
    // half-down in a sales demo.
    const status = onlineRoll < onlineBias ? 'online' : onlineRoll > 0.97 ? 'offline' : 'degraded';
    const padded = String(i + 1).padStart(3, '0');
    const sn = `${profile.prefix}-${region.state}-${padded}`;
    const jitterLat = lat + (rand() - 0.5) * (profile.spread ?? 0.5);
    const jitterLon = lon + (rand() - 0.5) * (profile.spread ?? 0.5);
    const monthIdx = (i % 9) + 1;
    const allocGB = parseInt(siteType.planTier.match(/\d+/)?.[0] || '500');
    // Usage as a fraction of allocation so utilization reads sensibly: most
    // sites comfortably under cap, ~12% running hot (85%+) for realism.
    const util = rand() < 0.09 ? between(0.86, 1.03) : between(profile.util[0], Math.min(profile.util[1], 0.84));
    const usageGB = +(allocGB * util).toFixed(1);

    return {
      deviceId: `UT-${sn}-${(i * 7919 + 100000).toString(36).toUpperCase()}`,
      routerId: `RT-${(i * 6151 + (profile.seed * 13)).toString(36).toUpperCase()}`,
      userTerminalId: `term-${profile.prefix.toLowerCase()}-${padded}`,
      kitId: `KIT-${profile.prefix}-${padded}`,
      serviceLineNumber: `SL-${profile.prefix}-${padded}`,
      model: siteType.kit,
      siteType: siteType.id,
      siteTypeLabel: siteType.label,
      siteName: typeof siteType.name === 'function' ? siteType.name(cityName, monthIdx, i) : `${cityName} ${siteType.label} #${monthIdx}`,
      region: region.name,
      state: region.state,
      city: cityName,
      addressDescription: `${Math.floor(between(100, 9999))} ${pick(profile.streets || ['Main', 'Industrial', 'Commerce'])} ${pick(['St', 'Blvd', 'Way', 'Rd', 'Hwy'])}, ${cityName}, ${region.state}`,
      planTier: siteType.planTier,
      sla: sla.id,
      slaLabel: sla.label,
      uptimeTarget: sla.uptimeTarget,
      latencyTargetMs: sla.latencyTargetMs,
      status,
      online: status === 'online' || status === 'degraded',
      lastHeartbeatAt: status === 'offline'
        ? new Date(Date.now() - between(1, 48) * 3600 * 1000).toISOString()
        : new Date(Date.now() - between(0, 60) * 1000).toISOString(),
      obstructionPercent: +(rand() * (profile.obstructionMax ?? 4)).toFixed(1),
      uptimeHours: Math.floor(between(48, 8000)),
      lat: jitterLat,
      lon: jitterLon,
      popPingLatencyMs: status === 'offline' ? null
        : status === 'degraded'
          ? +between(sla.latencyTargetMs + 6, sla.latencyTargetMs + 42).toFixed(1)
          : +between(Math.max(8, sla.latencyTargetMs - 18), sla.latencyTargetMs - 2).toFixed(1),
      downlinkThroughputMbps: status === 'offline' ? null : +between(120, 290).toFixed(0),
      uplinkThroughputMbps: status === 'offline' ? null : +between(12, 32).toFixed(0),
      signalQualityPercent: status === 'offline' ? null : Math.floor(between(status === 'degraded' ? 55 : 78, 96)),
      snrDb: status === 'offline' ? null : +between(8, 16).toFixed(1),
      softwareVersion: pick(['2026.05.20.mr11290', '2026.05.20.mr11290', '2026.05.10.mr11234', '2026.04.22.mr11187', '2026.04.10.mr11122']),
      tags: Array.from(new Set([
        siteType.inMotion ? 'in-motion' : (profile.tag || 'production'),
        pick(['tier-1', 'tier-2', 'priority-data', profile.tag || 'production']),
      ])).slice(0, 2),
      monthlyUsageGB: usageGB,
      monthlyAllocationGB: allocGB,
      monthlyCostUsd: +between(profile.costRange[0], profile.costRange[1]).toFixed(2),
      activatedAt: new Date(Date.now() - between(30, 720) * 86400 * 1000).toISOString(),
    };
  });
}

function computeStats(terms) {
  const online = terms.filter((t) => t.status === 'online').length;
  const degraded = terms.filter((t) => t.status === 'degraded').length;
  const offline = terms.filter((t) => t.status === 'offline').length;
  const latencies = terms.filter((t) => t.popPingLatencyMs).map((t) => t.popPingLatencyMs);
  const avgLatency = latencies.length ? latencies.reduce((a, b) => a + b, 0) / latencies.length : 0;
  const totalUsageGB = terms.reduce((s, t) => s + t.monthlyUsageGB, 0);
  const totalAllocationGB = terms.reduce((s, t) => s + t.monthlyAllocationGB, 0);
  const totalCostUsd = terms.reduce((s, t) => s + t.monthlyCostUsd, 0);
  const slaBreaches = terms.filter((t) => t.popPingLatencyMs && t.popPingLatencyMs > t.latencyTargetMs).length;
  return { total: terms.length, online, degraded, offline, avgLatency, totalUsageGB, totalAllocationGB, totalCostUsd, slaBreaches };
}

function groupsFor(terms) {
  const byRegion = {};
  const byType = {};
  const bySla = {};
  for (const t of terms) {
    byRegion[t.region] = (byRegion[t.region] || 0) + 1;
    byType[t.siteTypeLabel] = (byType[t.siteTypeLabel] || 0) + 1;
    bySla[t.slaLabel] = bySla[t.slaLabel] || { count: 0, target: t.uptimeTarget, latency: t.latencyTargetMs };
    bySla[t.slaLabel].count += 1;
  }
  return [
    ...Object.entries(byRegion).map(([name, n]) => ({ id: `region-${name}`, name, type: 'region', description: `All sites in ${name}`, terminalCount: n })),
    ...Object.entries(byType).map(([name, n]) => ({ id: `type-${name}`, name, type: 'site-type', description: `${name} deployments`, terminalCount: n })),
    ...Object.entries(bySla).map(([name, v]) => ({ id: `sla-${name}`, name, type: 'sla-tier', description: `${v.target}% uptime · ${v.latency}ms latency target`, terminalCount: v.count })),
  ];
}

// ─── SLA tier presets ────────────────────────────────────────
const SLA_CRITICAL = { id: 'critical', label: 'Mission critical', uptimeTarget: 99.95, latencyTargetMs: 35 };
const SLA_STANDARD = { id: 'standard', label: 'Business standard', uptimeTarget: 99.5, latencyTargetMs: 60 };
const SLA_BASIC    = { id: 'basic',    label: 'Basic / backup',    uptimeTarget: 99.0, latencyTargetMs: 90 };
const SLA_MOBILE   = { id: 'mobile',   label: 'Mobile Priority',   uptimeTarget: 99.0, latencyTargetMs: 70 };

// Reefer trailer plate suffixes for flavor (used by the logistics fleet's
// site-name fn — declared up here so it's defined before buildFleet runs).
const RF = ['A18', 'B22', 'C07', 'D31', 'E14', 'F09', 'G26', 'H03'];

// ════════════════════════════════════════════════════════════
// 1. OIL & GAS — Globaltrade Energy (LARGE, ~64)
// ════════════════════════════════════════════════════════════
const OILGAS_TERMINALS = buildFleet({
  prefix: 'OG', count: 64, seed: 101, onlineBias: 0.95, obstructionMax: 1.5, spread: 0.8,
  util: [0.42, 0.8], costRange: [320, 720],
  streets: ['Lease', 'Field', 'County Rd', 'Access', 'Frontage'],
  regions: [
    { name: 'Permian Basin',   state: 'TX', cities: [['Midland', 31.99, -102.07], ['Odessa', 31.84, -102.37], ['Pecos', 31.42, -103.49]] },
    { name: 'Permian (NM)',    state: 'NM', cities: [['Hobbs', 32.70, -103.13], ['Carlsbad', 32.42, -104.23]] },
    { name: 'Bakken',          state: 'ND', cities: [['Williston', 48.15, -103.62], ['Watford City', 47.80, -103.28]] },
    { name: 'Anadarko Basin',  state: 'OK', cities: [['Elk City', 35.41, -99.40], ['Woodward', 36.43, -99.39]] },
    { name: 'Eagle Ford',      state: 'TX', cities: [['Cotulla', 28.44, -99.23], ['Karnes City', 28.88, -97.90]] },
    { name: 'Gulf of Mexico',  state: 'GOM', cities: [['GoM Block 252', 28.74, -88.37], ['GoM Block 807', 27.20, -90.05]] },
  ],
  siteTypes: [
    { id: 'wellsite',    label: 'Wellsite',            kit: 'High Performance Kit', planTier: 'Mobility Plus 2TB', name: (c, m) => `${c} Wellsite #${m}` },
    { id: 'rig',         label: 'Drilling rig',        kit: 'High Performance Kit', planTier: 'Mobility Plus 5TB', name: (c, m) => `${c} Rig ${m}` },
    { id: 'compressor',  label: 'Compressor station',  kit: 'Performance Kit (Gen 3)', planTier: 'Local 1TB',      name: (c, m) => `${c} Compressor #${m}` },
    { id: 'gathering',   label: 'Gathering facility',  kit: 'Performance Kit (Gen 3)', planTier: 'Local 1TB',      name: (c, m) => `${c} Gathering #${m}` },
    { id: 'scada',       label: 'SCADA / telemetry',   kit: 'Standard Kit',         planTier: 'Local 500GB',       name: (c, m) => `${c} SCADA Remote #${m}` },
    { id: 'offshore',    label: 'Offshore platform',   kit: 'High Performance Kit', planTier: 'Global Priority 5TB', inMotion: false, name: (c) => `${c} Platform` },
    { id: 'mancamp',     label: 'Man camp',            kit: 'Performance Kit (Gen 3)', planTier: 'Local 1TB',      name: (c, m) => `${c} Man Camp #${m}` },
  ],
  slaMix: [SLA_CRITICAL, SLA_CRITICAL, SLA_STANDARD],
  tag: 'production',
});

// ════════════════════════════════════════════════════════════
// 2. LOGISTICS / MOBILE FLEET — Continental Freight Lines (MEDIUM, ~24)
// ════════════════════════════════════════════════════════════
const LOGISTICS_TERMINALS = buildFleet({
  prefix: 'FL', count: 24, seed: 202, onlineBias: 0.95, obstructionMax: 3.5, spread: 1.6,
  util: [0.35, 0.78], costRange: [180, 360],
  streets: ['Interstate', 'Logistics', 'Freight', 'Distribution'],
  regions: [
    { name: 'Midwest corridor',  state: 'OH', cities: [['Columbus', 39.96, -82.99], ['Indianapolis', 39.77, -86.16]] },
    { name: 'Southeast corridor',state: 'TN', cities: [['Memphis', 35.15, -90.05], ['Nashville', 36.16, -86.78], ['Atlanta', 33.75, -84.39]] },
    { name: 'Texas Triangle',    state: 'TX', cities: [['Dallas', 32.78, -96.80], ['San Antonio', 29.42, -98.49]] },
    { name: 'Mountain West',     state: 'UT', cities: [['Salt Lake City', 40.76, -111.89], ['Denver', 39.74, -104.99]] },
    { name: 'Pacific corridor',  state: 'CA', cities: [['Bakersfield', 35.37, -119.02], ['Sacramento', 38.58, -121.49]] },
  ],
  siteTypes: [
    { id: 'longhaul',  label: 'Long-haul tractor', kit: 'Mini Kit',     planTier: 'Mobile Priority 250GB', inMotion: true, name: (c, m, i) => `Tractor ${1400 + i} · ${c} lane` },
    { id: 'reefer',    label: 'Reefer trailer',    kit: 'Mini Kit',     planTier: 'Mobile Priority 250GB', inMotion: true, name: (c, m, i) => `Reefer ${RF[i % RF.length]} · ${c}` },
    { id: 'regional',  label: 'Regional box truck',kit: 'Mini Kit',     planTier: 'Mobile 50GB',           inMotion: true, name: (c, m, i) => `Box truck ${300 + i} · ${c}` },
    { id: 'hostler',   label: 'Yard hostler',      kit: 'Standard Kit', planTier: 'Local 500GB',           name: (c, m) => `${c} Yard hostler #${m}` },
    { id: 'command',   label: 'Mobile command',    kit: 'Performance Kit (Gen 3)', planTier: 'Mobile Priority 1TB', inMotion: true, name: (c, m) => `${c} Mobile command #${m}` },
  ],
  slaMix: [SLA_MOBILE, SLA_MOBILE, SLA_STANDARD],
  tag: 'in-motion',
});

// ════════════════════════════════════════════════════════════
// 3. RETAIL — Globaltrade Retail (SMALL, ~10) · Starlink as failover
// ════════════════════════════════════════════════════════════
const RETAIL_TERMINALS = buildFleet({
  prefix: 'RT', count: 10, seed: 303, onlineBias: 0.95, obstructionMax: 4.5, spread: 0.4,
  util: [0.06, 0.35], costRange: [110, 190],   // low Starlink use — it's the backup link
  streets: ['Market', 'Commerce', 'Center', 'Plaza', 'Retail'],
  regions: [
    { name: 'Texas metros',   state: 'TX', cities: [['Plano', 33.02, -96.70], ['Frisco', 33.15, -96.82], ['Sugar Land', 29.62, -95.63]] },
    { name: 'Southeast metros',state: 'GA', cities: [['Marietta', 33.95, -84.55], ['Alpharetta', 34.07, -84.29]] },
    { name: 'Carolinas',      state: 'NC', cities: [['Cary', 35.79, -78.78], ['Huntersville', 35.41, -80.84]] },
  ],
  siteTypes: [
    { id: 'store',     label: 'Retail store',       kit: 'Standard Kit', planTier: 'Local 50GB',  name: (c, m) => `Globaltrade Retail — ${c} #${m}` },
    { id: 'pharmacy',  label: 'Pharmacy counter',   kit: 'Standard Kit', planTier: 'Local 50GB',  name: (c, m) => `Globaltrade Pharmacy — ${c} #${m}` },
    { id: 'express',   label: 'Express / kiosk',    kit: 'Mini Kit',     planTier: 'Local 50GB',  name: (c, m) => `Globaltrade Express — ${c} #${m}` },
  ],
  slaMix: [SLA_STANDARD, SLA_BASIC, SLA_BASIC],
  tag: 'failover',
});

// ─── Account metadata per vertical ───────────────────────────
function accountFor(num, name, contact, vertical, slaTier) {
  return {
    accountNumber: num,
    accountName: name,
    businessName: name,
    vertical,
    slaTier,
    loaStatus: 'signed',
    loaSignedAt: '2025-08-01T00:00:00Z',
    createdAt: '2025-07-15T00:00:00Z',
    authorizedRepresentative: contact,
  };
}

// Keyed by the ACCOUNT NUMBERS the rest of the app already uses:
//   • The three Globaltrade Holdings subsidiaries (org-model.js) so the
//     Org-switcher "Switch" lands each one on its OWN fleet (was: all
//     three aliased to the same logistics dataset — the bug the user hit).
//   • Each is ALSO reachable standalone via admin "View as customer".
//
// LARGE  → Globaltrade Energy (Oil & Gas)   · ACC-DEMO-CTO-50FLEET-01 + ACC-CTO-50-FLEET
// MEDIUM → Globaltrade Logistics (Fleet)    · ACC-GLOBAL-FIELD-03
// SMALL  → Globaltrade Retail (failover)    · ACC-GLOBAL-RETAIL-02
export const VERTICALS = {
  // ── LARGE: Oil & Gas — the flagship "full" demo (cto@globaltrade login) ──
  'ACC-DEMO-CTO-50FLEET-01': {
    account: accountFor('ACC-DEMO-CTO-50FLEET-01', 'Globaltrade Energy',
      { name: 'Morgan Patel', email: 'cto@globaltrade.example', phone: '+1-432-555-0142' },
      'Oil & Gas', 'Mission Critical'),
    terminals: OILGAS_TERMINALS,
    stats: computeStats(OILGAS_TERMINALS),
    groups: groupsFor(OILGAS_TERMINALS),
  },
  // ── MEDIUM: Logistics / mobile fleet mounted on vehicles ──
  'ACC-GLOBAL-FIELD-03': {
    account: accountFor('ACC-GLOBAL-FIELD-03', 'Globaltrade Logistics',
      { name: 'Marcus Hale', email: 'fleet.ops@globaltrade.example', phone: '+1-614-555-0188' },
      'Logistics & Mobile Fleet', 'Mobile Priority'),
    terminals: LOGISTICS_TERMINALS,
    stats: computeStats(LOGISTICS_TERMINALS),
    groups: groupsFor(LOGISTICS_TERMINALS),
  },
  // ── SMALL: Retail shops using Starlink as failover ──
  'ACC-GLOBAL-RETAIL-02': {
    account: accountFor('ACC-GLOBAL-RETAIL-02', 'Globaltrade Retail',
      { name: 'Priya Nair', email: 'retail.it@globaltrade.example', phone: '+1-972-555-0110' },
      'Retail (Starlink failover)', 'Business Standard'),
    terminals: RETAIL_TERMINALS,
    stats: computeStats(RETAIL_TERMINALS),
    groups: groupsFor(RETAIL_TERMINALS),
  },
};

// Legacy alias → flagship Oil & Gas fleet (same as ACC-DEMO-CTO-50FLEET-01).
const VERTICAL_ALIASES = { 'ACC-CTO-50-FLEET': 'ACC-DEMO-CTO-50FLEET-01' };

/** Resolve an account number (or alias) → its vertical fleet bundle, or null. */
export function getVertical(accountNumber) {
  if (!accountNumber) return null;
  const key = VERTICAL_ALIASES[accountNumber] || accountNumber;
  return VERTICALS[key] || null;
}

export const VERTICAL_ACCOUNT_NUMBERS = Object.keys(VERTICALS);

/** All terminals across every vertical — used to resolve a deviceId → its fleet. */
export function allVerticalTerminals() {
  return Object.values(VERTICALS).flatMap((v) => v.terminals);
}
